/** macOS / iOS: primary chord uses ⌘ (matches `metaKey` in AppShell shortcuts). */
const GLYPH_CMD = "\u2318";
/** Control (non-Mac menus). */
const GLYPH_CTRL = "\u2303";
const GLYPH_ALT = "\u2325";
const GLYPH_SHIFT = "\u21e7";

function formatKeyToken(key: string): string {
  switch (key) {
    case "ArrowUp":
    case "Up":
      return "\u2191";
    case "ArrowDown":
    case "Down":
      return "\u2193";
    case "ArrowLeft":
    case "Left":
      return "\u2190";
    case "ArrowRight":
    case "Right":
      return "\u2192";
    case "Del":
      return "\u2326";
    case "Space":
      return "\u2423";
    default:
      if (key.length === 1 && /[a-z]/i.test(key)) return key.toUpperCase();
      return key;
  }
}

function stripLeadingModifiers(s: string): { ctrl: boolean; alt: boolean; shift: boolean; rest: string } {
  let ctrl = false;
  let alt = false;
  let shift = false;
  let rest = s;
  let changed = true;
  while (changed) {
    changed = false;
    if (rest.startsWith("Ctrl+")) {
      ctrl = true;
      rest = rest.slice(5);
      changed = true;
    } else if (rest.startsWith("Alt+")) {
      alt = true;
      rest = rest.slice(4);
      changed = true;
    } else if (rest.startsWith("Shift+")) {
      shift = true;
      rest = rest.slice(6);
      changed = true;
    }
  }
  return { ctrl, alt, shift, rest };
}

/**
 * Renders a canonical Notepad-style shortcut (e.g. `Ctrl+Shift+F`) using modifier glyphs
 * instead of the words Ctrl / Alt / Shift.
 *
 * @param isMac — when true, `Ctrl` maps to ⌘ (Command), matching in-app `metaKey` handling.
 */
export function formatMenuShortcutDisplay(canonical: string, isMac: boolean): string {
  const t = canonical.trim();
  if (!t) return "";

  const { ctrl, alt, shift, rest } = stripLeadingModifiers(t);
  const primary = ctrl ? (isMac ? GLYPH_CMD : GLYPH_CTRL) : "";
  let mods = "";
  if (shift) mods += GLYPH_SHIFT;
  if (alt) mods += GLYPH_ALT;
  mods += primary;

  const key = formatKeyToken(rest);
  return `${mods}${key}`;
}

export function menuShortcutIsMacLike(): boolean {
  if (typeof navigator === "undefined") return false;
  const p = navigator.platform ?? "";
  return /Mac|iPhone|iPad|iPod/.test(p);
}

/** Shortcut text shown in the menubar (glyphs); use {@link canonical} for `aria-label`. */
export function formatMenuShortcutForUi(canonical: string): string {
  return formatMenuShortcutDisplay(canonical, menuShortcutIsMacLike());
}
